<template>
    <div>
        <!-- Grid view -->
        <div v-if="viewMode === 'grid'" class="grid gap-4" style="grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));">
            <div
                v-for="item in items"
                :key="item.id"
                class="media-card group relative bg-white rounded-lg border overflow-hidden cursor-pointer transition-all flex flex-col"
                :class="{
                    'ring-2 ring-indigo-500 border-indigo-500 shadow-md': isSelected(item),
                    'border-gray-200 hover:shadow-md hover:border-gray-300': !isSelected(item),
                }"
                @click.exact="$emit('select', item)"
                @click.ctrl="$emit('toggle-select', item)"
                @click.meta="$emit('toggle-select', item)"
                @click.shift="$emit('toggle-select', item)"
            >
                <!-- Thumbnail -->
                <div class="bg-gray-100 flex items-center justify-center overflow-hidden" style="height: 140px;">
                    <img
                        v-if="item.thumbnail && !failedImages[item.id]"
                        :src="item.thumbnail"
                        :alt="item.name"
                        class="w-full h-full object-contain"
                        loading="lazy"
                        @error="onImageError(item)"
                    />
                    <div v-else class="flex flex-col items-center justify-center">
                        <i :class="[getFileIcon(item), fileIconColor(item)]" class="text-3xl"></i>
                        <span v-if="item.type !== 'image' && !iconHasLabel(item)" class="text-xs mt-1 text-gray-400 font-medium uppercase">{{ fileExt(item) || item.type }}</span>
                    </div>
                </div>

                <!-- Info — always at bottom -->
                <div class="mt-auto px-2.5 py-2 border-t border-gray-100">
                    <div class="text-xs font-medium text-gray-700 truncate" :title="item.file_name">{{ item.name }}</div>
                    <div class="flex items-center gap-1.5 mt-0.5">
                        <span class="text-xs text-gray-400">{{ formatSize(item.size) }}</span>
                        <span
                            class="inline-flex items-center ml-auto rounded-full px-1.5 py-0 text-xs leading-4"
                            :class="item.source === 'spatie' ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-600'"
                            style="font-size: 10px;"
                        >
                            {{ item.source === 'spatie' ? 'Content' : 'Upload' }}
                        </span>
                    </div>
                </div>

                <!-- Usage indicator -->
                <div
                    v-if="item.usage_count === 0"
                    class="absolute top-1.5 right-1.5 bg-orange-100 text-orange-600 text-xs px-1.5 py-0.5 rounded-full font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                    style="font-size: 10px;"
                >
                    Ongebruikt
                </div>

                <!-- Selection check -->
                <div
                    v-if="isMultiSelected(item)"
                    class="absolute top-1.5 left-1.5 w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center shadow-sm"
                >
                    <i class="fa fa-check text-white" style="font-size: 10px;"></i>
                </div>
            </div>
        </div>

        <!-- List view -->
        <div v-else class="overflow-hidden rounded-lg border border-gray-200">
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                        <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style="width: 50px;"></th>
                        <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Naam</th>
                        <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grootte</th>
                        <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bron</th>
                        <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
                        <th class="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style="width: 80px;">Gebruik</th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-100">
                    <tr
                        v-for="item in items"
                        :key="item.id"
                        class="cursor-pointer transition-colors"
                        :class="isSelected(item) ? 'bg-indigo-50' : 'hover:bg-gray-50'"
                        @click.exact="$emit('select', item)"
                        @click.ctrl="$emit('toggle-select', item)"
                        @click.meta="$emit('toggle-select', item)"
                        @click.shift="$emit('toggle-select', item)"
                    >
                        <td class="px-3 py-2 text-center align-middle">
                            <img
                                v-if="item.thumbnail && !failedImages[item.id]"
                                :src="item.thumbnail"
                                class="rounded"
                                style="width: 36px; height: 36px; object-fit: cover;"
                                loading="lazy"
                                @error="onImageError(item)"
                            />
                            <div v-else class="w-9 h-9 rounded bg-gray-50 flex items-center justify-center">
                                <i :class="[getFileIcon(item), fileIconColor(item)]"></i>
                            </div>
                        </td>
                        <td class="px-3 py-2 align-middle">
                            <div class="text-sm font-medium text-gray-900">{{ item.name }}</div>
                            <div class="text-xs text-gray-400 truncate" style="max-width: 250px;">{{ item.file_name }}</div>
                        </td>
                        <td class="px-3 py-2 align-middle text-sm text-gray-500 uppercase">{{ fileExt(item) || item.type }}</td>
                        <td class="px-3 py-2 align-middle text-sm text-gray-500">{{ formatSize(item.size) }}</td>
                        <td class="px-3 py-2 align-middle">
                            <span
                                class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                                :class="item.source === 'spatie' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'"
                            >
                                {{ item.source === 'spatie' ? 'Content' : 'Upload' }}
                            </span>
                        </td>
                        <td class="px-3 py-2 align-middle text-sm text-gray-500">{{ formatDate(item.created_at) }}</td>
                        <td class="px-3 py-2 align-middle text-center">
                            <span
                                v-if="item.usage_count > 0"
                                class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-indigo-50 text-indigo-700"
                            >
                                {{ item.usage_count }}
                            </span>
                            <span v-else class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-orange-50 text-orange-600">
                                0
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="text-center py-8">
            <i class="far fa-circle-notch fa-spin text-2xl text-gray-300"></i>
        </div>

        <!-- Empty state -->
        <div v-if="!loading && items.length === 0" class="text-center py-20">
            <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <i class="far fa-photo-video text-2xl text-gray-400"></i>
            </div>
            <h3 class="text-sm font-medium text-gray-900 mb-1">Geen bestanden gevonden</h3>
            <p class="text-sm text-gray-500">Probeer een andere zoekterm of pas de filters aan.</p>
        </div>

        <!-- Infinite scroll sentinel -->
        <div v-if="hasMore && !loading" ref="sentinel" class="h-4"></div>
    </div>
</template>

<script>
export default {
    name: 'MediaGrid',

    props: {
        items: { type: Array, required: true },
        viewMode: { type: String, default: 'grid' },
        selectedItem: { type: Object, default: null },
        selectedItems: { type: Array, default: function () { return []; } },
        loading: { type: Boolean, default: false },
        hasMore: { type: Boolean, default: false },
    },

    data() {
        return {
            observer: null,
            failedImages: {},
        };
    },

    mounted() {
        this.setupIntersectionObserver();
    },

    updated() {
        this.setupIntersectionObserver();
    },

    beforeDestroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    },

    methods: {
        setupIntersectionObserver() {
            var self = this;

            if (this.observer) {
                this.observer.disconnect();
            }

            if (!this.$refs.sentinel) return;

            this.observer = new IntersectionObserver(function (entries) {
                if (entries[0].isIntersecting) {
                    self.$emit('load-more');
                }
            }, { threshold: 0.1 });

            this.observer.observe(this.$refs.sentinel);
        },

        onImageError(item) {
            this.$set(this.failedImages, item.id, true);
        },

        isSelected(item) {
            return this.selectedItem && this.selectedItem.id === item.id;
        },

        isMultiSelected(item) {
            return this.selectedItems.length > 1 && this.selectedItems.some(function (i) { return i.id === item.id; });
        },

        getTypeIcon(type) {
            var icons = {
                image: 'far fa-image',
                video: 'far fa-video',
                audio: 'far fa-music',
                document: 'far fa-file-alt',
                archive: 'far fa-file-archive',
                other: 'far fa-file',
            };
            return icons[type] || 'far fa-file';
        },

        // File extension (lowercase) from the filename, e.g. "pdf", "docx".
        fileExt(item) {
            var name = item.file_name || item.name || '';
            var m = name.match(/\.([a-z0-9]+)$/i);
            return m ? m[1].toLowerCase() : '';
        },

        // Per-extension file icon (pdf/word/excel/…), falling back to the broad type icon.
        getFileIcon(item) {
            if (item.type === 'image') {
                return 'far fa-image';
            }

            var map = {
                pdf: 'far fa-file-pdf',
                doc: 'far fa-file-word', docx: 'far fa-file-word',
                xls: 'far fa-file-excel', xlsx: 'far fa-file-excel', csv: 'far fa-file-csv',
                ppt: 'far fa-file-powerpoint', pptx: 'far fa-file-powerpoint',
                zip: 'far fa-file-archive', rar: 'far fa-file-archive', '7z': 'far fa-file-archive',
                txt: 'far fa-file-alt', rtf: 'far fa-file-alt',
                svg: 'far fa-file-image',
                mp4: 'far fa-file-video', mov: 'far fa-file-video', webm: 'far fa-file-video',
                mp3: 'far fa-file-audio', wav: 'far fa-file-audio',
            };

            return map[this.fileExt(item)] || this.getTypeIcon(item.type);
        },

        // These FA file icons already render the type text (PDF/DOC/XLS/PPT/CSV),
        // so we don't repeat it as a label below the icon.
        iconHasLabel(item) {
            return ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'ppt', 'pptx'].indexOf(this.fileExt(item)) !== -1;
        },

        // Brand-ish colour per file type. Literal classes so Tailwind's JIT keeps them.
        fileIconColor(item) {
            var ext = this.fileExt(item);
            if (ext === 'pdf') return 'text-red-400';
            if (['doc', 'docx'].indexOf(ext) !== -1) return 'text-blue-500';
            if (['xls', 'xlsx', 'csv'].indexOf(ext) !== -1) return 'text-green-500';
            if (['ppt', 'pptx'].indexOf(ext) !== -1) return 'text-orange-400';
            if (['zip', 'rar', '7z'].indexOf(ext) !== -1) return 'text-yellow-500';
            return 'text-gray-300';
        },

        formatSize(bytes) {
            if (!bytes) return '0 B';
            var units = ['B', 'KB', 'MB', 'GB'];
            var i = 0;
            var size = bytes;
            while (size >= 1024 && i < units.length - 1) {
                size /= 1024;
                i++;
            }
            return size.toFixed(1) + ' ' + units[i];
        },

        formatDate(dateStr) {
            if (!dateStr) return '';
            var d = new Date(dateStr);
            return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
        },
    },
};
</script>

<style scoped>
.media-card:hover {
    transform: translateY(-1px);
}
</style>
